import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  LinearProgress,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import {
  deriveCompanyId,
  deleteUserAiAccess,
  fetchAiAccessConfig,
  fetchAiUsageEvents,
  fetchAiUsageLimits,
  fetchAiUsageSummary,
  normalizeSuperuserCargo,
  resolveAgentBaseUrl,
  upsertGlobalAiAccess,
  upsertAiUsageLimit,
  upsertUserAiAccess,
} from "./aiUsageClient";
import { useServer } from "../../context/ServerContext";
import { ObtenerUsuarios } from "../../utils/apiServer";

const panelSx = {
  border: "0.01vw solid rgba(233, 78, 26, 0.24)",
  borderRadius: "1vw",
  background:
    "radial-gradient(circle at top right, rgba(233, 78, 26, 0.12), transparent 24%), linear-gradient(180deg, rgba(15, 23, 38, 0.96), rgba(8, 14, 24, 0.96))",
  boxShadow: "inset 0 0 0 0.01vw rgba(255,255,255,0.03)",
};

const inputSx = {
  "& .MuiInputBase-root": {
    minHeight: "4.6vh",
    borderRadius: "0.8vw",
    background: "rgba(255,255,255,0.03)",
    color: "#d8e0ea",
    fontSize: "0.72vw",
  },
  "& .MuiInputBase-input": {
    py: "1.15vh",
  },
  "& .MuiInputLabel-root": {
    color: "#8fa0b5",
    fontSize: "0.72vw",
  },
  "& .MuiInputLabel-root.MuiInputLabel-shrink": {
    transform: "translate(14px, -9px) scale(0.75)",
    backgroundColor: "rgba(15, 23, 38, 0.96)",
    px: "0.25vw",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(233, 78, 26, 0.28)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(233, 78, 26, 0.55)",
  },
  "& .MuiSelect-icon, & .MuiSvgIcon-root": {
    color: "#d8e0ea",
  },
  "& input[type='month']::-webkit-calendar-picker-indicator": {
    filter: "invert(1) brightness(1.8)",
    opacity: 0.85,
  },
};

const autocompleteSx = {
  ...inputSx,
  "& .MuiAutocomplete-popupIndicator": { color: "#d8e0ea" },
  "& .MuiAutocomplete-clearIndicator": { color: "#8fa0b5" },
};

const thSx = {
  textAlign: "left",
  borderBottom: "0.01vw solid rgba(233, 78, 26, 0.22)",
  padding: "0.85vh 0.8vw",
  fontSize: "0.64vw",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "#8fa0b5",
  position: "sticky",
  top: 0,
  background: "rgba(8, 14, 24, 0.98)",
  zIndex: 1,
};

const tdSx = {
  padding: "0.9vh 0.8vw",
  borderBottom: "0.01vw solid rgba(233, 78, 26, 0.12)",
  fontSize: "0.7vw",
  color: "#d4dde8",
  verticalAlign: "top",
};

function currentMonth() {
  return new Date().toISOString().slice(0, 7);
}

function money(value) {
  return `$${Number(value || 0).toFixed(4)}`;
}

function compactNumber(value) {
  return new Intl.NumberFormat("es-CL", { maximumFractionDigits: 0 }).format(Number(value || 0));
}

function emptyLimit(scope, identifier) {
  return {
    scope,
    identifier,
    enabled: true,
    hard_limit: true,
    monthly_budget_usd: "",
    warn_at_percent: 80,
  };
}

function normalizeLimit(limit, scope, identifier) {
  if (!limit) return emptyLimit(scope, identifier);
  return {
    scope,
    identifier,
    enabled: Boolean(limit.enabled),
    hard_limit: Boolean(limit.hard_limit),
    monthly_budget_usd:
      limit.monthly_budget_usd === null || limit.monthly_budget_usd === undefined
        ? ""
        : String(limit.monthly_budget_usd),
    warn_at_percent:
      limit.warn_at_percent === null || limit.warn_at_percent === undefined
        ? 80
        : Number(limit.warn_at_percent),
  };
}

export default function AiUsageDashboard({ translate, cargo }) {
  const t = useCallback((key, fallback) => {
    const value = typeof translate === "function" ? translate(key) : key;
    return value && value !== key ? value : fallback;
  }, [translate]);

  const { servidor, aiWebhookUrl, refreshAiAccess } = useServer();
  const username = localStorage.getItem("username") || "";
  const activeCargo = cargo || localStorage.getItem("cargo") || "";
  const allowed = normalizeSuperuserCargo(activeCargo);
  const companyId = useMemo(() => deriveCompanyId(servidor), [servidor]);
  const agentBaseUrl = useMemo(() => resolveAgentBaseUrl(aiWebhookUrl), [aiWebhookUrl]);

  const [month, setMonth] = useState(currentMonth());
  const [serviceFilter, setServiceFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [limits, setLimits] = useState([]);
  const [accessConfig, setAccessConfig] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [companyLimit, setCompanyLimit] = useState(emptyLimit("company", companyId));
  const [userLimit, setUserLimit] = useState(emptyLimit("user", ""));
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const rows = useMemo(() => summary?.rows || [], [summary]);
  const totals = useMemo(() => summary?.totals || {}, [summary]);

  const userRows = useMemo(() => {
    const byUser = new Map();
    rows.forEach((row) => {
      const key = row.username || "(sin usuario)";
      const current = byUser.get(key) || {
        username: key,
        services: new Set(),
        models: new Set(),
        openai_call_count: 0,
        request_count: 0,
        total_tokens: 0,
        cached_tokens: 0,
        estimated_cost_usd: 0,
      };
      current.services.add(row.service || "-");
      current.models.add(row.model || "-");
      current.openai_call_count += Number(row.openai_call_count || 0);
      current.request_count += Number(row.request_count || 0);
      current.total_tokens += Number(row.total_tokens || 0);
      current.cached_tokens += Number(row.cached_tokens || 0);
      current.estimated_cost_usd += Number(row.estimated_cost_usd || 0);
      byUser.set(key, current);
    });
    return Array.from(byUser.values())
      .map((row) => ({
        ...row,
        services: Array.from(row.services).join(", "),
        models: Array.from(row.models).join(", "),
      }))
      .sort((a, b) => b.estimated_cost_usd - a.estimated_cost_usd);
  }, [rows]);

  const serviceOptions = useMemo(
    () => Array.from(new Set(rows.map((row) => row.service).filter(Boolean))).sort(),
    [rows]
  );

  const userOptions = useMemo(() => {
    const byUsername = new Map();
    availableUsers.forEach((user) => {
      const userName = String(user?.username || "").trim();
      if (!userName) return;
      byUsername.set(userName, {
        username: userName,
        cargo: user.cargo || "",
        correo: user.correo || user.email || "",
      });
    });
    userRows.forEach((row) => {
      const userName = String(row.username || "").trim();
      if (!userName || userName === "(sin usuario)" || byUsername.has(userName)) return;
      byUsername.set(userName, { username: userName, cargo: "Con consumo", correo: "" });
    });
    return Array.from(byUsername.values()).sort((a, b) =>
      a.username.localeCompare(b.username)
    );
  }, [availableUsers, userRows]);

  const companyStoredLimit = useMemo(
    () => limits.find((limit) => limit.scope === "company" && limit.identifier === companyId),
    [companyId, limits]
  );

  const selectedUserStoredLimit = useMemo(
    () => limits.find((limit) => limit.scope === "user" && limit.identifier === selectedUser),
    [limits, selectedUser]
  );

  const accessOverridesByUser = useMemo(() => {
    const byUsername = new Map();
    (accessConfig?.users || []).forEach((entry) => {
      const userName = String(entry?.identifier || "").trim();
      if (userName) byUsername.set(userName, entry);
    });
    return byUsername;
  }, [accessConfig]);

  const globalAccess = accessConfig?.global?.access === "allow" ? "allow" : "deny";
  const selectedUserAccessEntry = selectedUser ? accessOverridesByUser.get(selectedUser) : null;
  const selectedUserAccessMode = selectedUserAccessEntry?.access || "inherit";
  const selectedUserEffectiveAccess =
    selectedUserAccessMode === "inherit" ? globalAccess : selectedUserAccessMode;
  const selectedUserEffectiveSource =
    selectedUserAccessMode === "inherit" ? "global" : "usuario";

  const usagePercent = useMemo(() => {
    const budget = Number(companyLimit.monthly_budget_usd || 0);
    if (!budget) return 0;
    return Math.min(100, (Number(totals.estimated_cost_usd || 0) / budget) * 100);
  }, [companyLimit.monthly_budget_usd, totals.estimated_cost_usd]);

  const selectedUserSpend = useMemo(() => {
    return userRows.find((row) => row.username === selectedUser)?.estimated_cost_usd || 0;
  }, [selectedUser, userRows]);

  const loadData = useCallback(async () => {
    if (!allowed || !agentBaseUrl) return;
    setLoading(true);
    setError("");
    try {
      const [summaryData, eventRows, limitRows, accessData] = await Promise.all([
        fetchAiUsageSummary({
          agentBaseUrl,
          month,
          companyId,
          username,
          cargo: activeCargo,
          service: serviceFilter,
        }),
        fetchAiUsageEvents({
          agentBaseUrl,
          month,
          companyId,
          username,
          cargo: activeCargo,
          service: serviceFilter,
          status: statusFilter,
          limit: 150,
        }),
        fetchAiUsageLimits({ agentBaseUrl, username, cargo: activeCargo }),
        fetchAiAccessConfig({ agentBaseUrl, username, cargo: activeCargo }),
      ]);
      setSummary(summaryData);
      setEvents(Array.isArray(eventRows) ? eventRows : []);
      setLimits(Array.isArray(limitRows) ? limitRows : []);
      setAccessConfig(accessData || null);
      setNotice("");
    } catch (err) {
      setError(err.message || "Error al cargar uso IA");
      setSummary(null);
      setEvents([]);
      setLimits([]);
      setAccessConfig(null);
    } finally {
      setLoading(false);
    }
  }, [activeCargo, agentBaseUrl, allowed, companyId, month, serviceFilter, statusFilter, username]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!allowed || !servidor) return undefined;
    let cancelled = false;
    ObtenerUsuarios(servidor)
      .then((usuarios) => {
        if (cancelled) return;
        setAvailableUsers(Array.isArray(usuarios) ? usuarios : []);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Error al cargar usuarios para limites IA:", err);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [allowed, servidor]);

  useEffect(() => {
    setCompanyLimit(normalizeLimit(companyStoredLimit, "company", companyId));
  }, [companyId, companyStoredLimit]);

  useEffect(() => {
    const fallbackUser = selectedUser || userOptions[0]?.username || "";
    if (!selectedUser && fallbackUser) setSelectedUser(fallbackUser);
    setUserLimit(normalizeLimit(selectedUserStoredLimit, "user", fallbackUser));
  }, [selectedUser, selectedUserStoredLimit, userOptions]);

  const saveLimit = async (limit) => {
    if (!limit.identifier) {
      setError("Selecciona o escribe un usuario para guardar limite.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await upsertAiUsageLimit({
        agentBaseUrl,
        username,
        cargo: activeCargo,
        payload: {
          ...limit,
          monthly_budget_usd:
            limit.monthly_budget_usd === "" ? null : Number(limit.monthly_budget_usd),
          warn_at_percent: Number(limit.warn_at_percent || 80),
        },
      });
      setNotice("Limite guardado.");
      await loadData();
    } catch (err) {
      setError(err.message || "No se pudo guardar el limite.");
    } finally {
      setLoading(false);
    }
  };

  const updateLimit = (setter, patch) => setter((current) => ({ ...current, ...patch }));

  const saveGlobalAccess = async (access) => {
    setLoading(true);
    setError("");
    try {
      await upsertGlobalAiAccess({ agentBaseUrl, access, username, cargo: activeCargo });
      setNotice(access === "allow" ? "Asistente habilitado globalmente." : "Asistente deshabilitado globalmente.");
      await loadData();
      if (typeof refreshAiAccess === "function") await refreshAiAccess();
    } catch (err) {
      setError(err.message || "No se pudo guardar el acceso global.");
    } finally {
      setLoading(false);
    }
  };

  const saveUserAccess = async (access) => {
    if (!selectedUser) {
      setError("Selecciona o escribe un usuario para configurar acceso.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      if (access === "inherit") {
        await deleteUserAiAccess({
          agentBaseUrl,
          targetUsername: selectedUser,
          username,
          cargo: activeCargo,
        });
      } else {
        await upsertUserAiAccess({
          agentBaseUrl,
          targetUsername: selectedUser,
          access,
          username,
          cargo: activeCargo,
        });
      }
      setNotice("Acceso de usuario guardado.");
      await loadData();
      if (typeof refreshAiAccess === "function") await refreshAiAccess();
    } catch (err) {
      setError(err.message || "No se pudo guardar el acceso de usuario.");
    } finally {
      setLoading(false);
    }
  };

  if (!allowed) {
    return (
      <Box sx={{ ...panelSx, p: "1.2vw" }}>
        <Typography sx={{ color: "#f2f5f8", fontSize: "1vw", fontWeight: 700 }}>
          {t("ai_usage_forbidden", "No tienes permisos para visualizar uso IA.")}
        </Typography>
      </Box>
    );
  }

  const summaryCards = [
    { label: "Costo USD", value: money(totals.estimated_cost_usd) },
    { label: "Tokens", value: compactNumber(totals.total_tokens) },
    { label: "Llamadas OpenAI", value: compactNumber(totals.openai_call_count) },
    { label: "Requests", value: compactNumber(totals.request_count) },
    { label: "Tokens cache", value: compactNumber(totals.cached_tokens) },
    { label: "Eventos sin usage", value: compactNumber(events.filter((event) => event.status === "usage_missing").length) },
  ];

  return (
    <Box sx={{ height: "100%", overflowY: "auto", pr: "0.2vw" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "1.3fr 0.9fr", gap: "1vw", mb: "1vw" }}>
        <Box sx={{ ...panelSx, p: "1vw" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: "1vw", alignItems: "flex-start" }}>
            <Box>
              <Typography sx={kickerSx}>AI Usage</Typography>
              <Typography sx={headingSx}>{t("ai_usage_title", "Uso IA")}</Typography>
              <Typography sx={mutedSx}>
                {companyId} · {agentBaseUrl || "sin agente configurado"}
              </Typography>
            </Box>
            <Tooltip title={t("refresh", "Actualizar")}>
              <span>
                <IconButton onClick={loadData} disabled={loading || !agentBaseUrl} sx={iconButtonSx}>
                  <RefreshRoundedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Box sx={{ mt: "1vw", display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "0.6vw" }}>
            {summaryCards.map((item) => (
              <Box key={item.label} sx={metricSx}>
                <Typography sx={metricLabelSx}>{item.label}</Typography>
                <Typography sx={metricValueSx}>{item.value}</Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ mt: "1vw" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: "0.45vh" }}>
              <Typography sx={metricLabelSx}>Presupuesto empresa</Typography>
              <Typography sx={metricLabelSx}>
                {companyLimit.monthly_budget_usd ? `${usagePercent.toFixed(1)}%` : "Sin cupo"}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={usagePercent}
              sx={{
                height: "0.8vh",
                borderRadius: "999px",
                backgroundColor: "rgba(255,255,255,0.08)",
                "& .MuiLinearProgress-bar": { backgroundColor: "#e84e0e" },
              }}
            />
          </Box>
        </Box>

        <Box sx={{ ...panelSx, p: "1vw", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.8vw", alignContent: "start" }}>
          <TextField
            label="Mes"
            type="month"
            value={month}
            onChange={(event) => setMonth(event.target.value || currentMonth())}
            sx={inputSx}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Servicio"
            select
            SelectProps={{ native: true }}
            value={serviceFilter}
            onChange={(event) => setServiceFilter(event.target.value)}
            sx={inputSx}
            size="small"
            InputLabelProps={{ shrink: true }}
          >
            <option value="">Todos</option>
            {serviceOptions.map((service) => (
              <option key={service} value={service}>{service}</option>
            ))}
          </TextField>
          <TextField
            label="Estado evento"
            select
            SelectProps={{ native: true }}
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            sx={inputSx}
            size="small"
            InputLabelProps={{ shrink: true }}
          >
            <option value="">Todos</option>
            <option value="success">success</option>
            <option value="usage_missing">usage_missing</option>
            <option value="failed">failed</option>
          </TextField>
          <Button onClick={loadData} disabled={loading} sx={primaryButtonSx}>
            Actualizar
          </Button>
          {(notice || error) && (
            <Typography sx={{ gridColumn: "1 / -1", color: error ? "#ff8d8d" : "#9ee6b8", fontSize: "0.72vw", fontWeight: 700 }}>
              {error || notice}
            </Typography>
          )}
        </Box>
      </Box>

      <AiAccessEditor
        globalAccess={globalAccess}
        selectedUser={selectedUser}
        users={userOptions}
        selectedAccessMode={selectedUserAccessMode}
        selectedEffectiveAccess={selectedUserEffectiveAccess}
        selectedEffectiveSource={selectedUserEffectiveSource}
        onGlobalChange={saveGlobalAccess}
        onUserChange={saveUserAccess}
        onUserSelect={setSelectedUser}
        loading={loading}
      />

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1vw", mb: "1vw" }}>
        <LimitEditor
          title="Limite empresa"
          description="Alerta visual al superar el umbral."
          limit={companyLimit}
          onChange={(patch) => updateLimit(setCompanyLimit, patch)}
          onSave={() => saveLimit(companyLimit)}
          loading={loading}
          lockIdentifier
          currentSpend={totals.estimated_cost_usd}
        />
        <LimitEditor
          title="Limite usuario"
          description="Alerta visual al superar el umbral."
          limit={userLimit}
          onChange={(patch) => {
            if (patch.identifier !== undefined) setSelectedUser(patch.identifier);
            updateLimit(setUserLimit, patch);
          }}
          onSave={() => saveLimit(userLimit)}
          loading={loading}
          users={userOptions}
          currentSpend={selectedUserSpend}
        />
      </Box>

      <Box sx={{ ...panelSx, p: "1vw", mb: "1vw" }}>
        <Typography sx={tableTitleSx}>Usuarios</Typography>
        <DataTable
          columns={[
            ["Usuario", "18%"],
            ["Servicio", "14%"],
            ["Modelo", "15%"],
            ["Costo", "12%"],
            ["Tokens", "13%"],
            ["Cache", "12%"],
            ["Calls", "8%"],
            ["Requests", "8%"],
          ]}
          emptyText="Sin consumo para el filtro seleccionado."
          rows={userRows.map((row) => [
            row.username,
            row.services,
            row.models,
            money(row.estimated_cost_usd),
            compactNumber(row.total_tokens),
            compactNumber(row.cached_tokens),
            compactNumber(row.openai_call_count),
            compactNumber(row.request_count),
          ])}
        />
      </Box>

      <Box sx={{ ...panelSx, p: "1vw" }}>
        <Typography sx={tableTitleSx}>Eventos recientes</Typography>
        <DataTable
          columns={[
            ["Fecha", "17%"],
            ["Usuario", "13%"],
            ["Servicio", "10%"],
            ["Modelo", "13%"],
            ["Status", "10%"],
            ["Tokens", "10%"],
            ["Costo", "10%"],
            ["Trace", "17%"],
          ]}
          emptyText="Sin eventos para el filtro seleccionado."
          rows={events.map((event) => [
            event.created_at ? new Date(event.created_at).toLocaleString("es-CL") : "-",
            event.username || "-",
            event.service || "-",
            event.model || "-",
            event.status || "-",
            compactNumber(event.total_tokens),
            money(event.estimated_cost_usd),
            event.trace_id || "-",
          ])}
        />
      </Box>
    </Box>
  );
}

function AiAccessEditor({
  globalAccess,
  selectedUser,
  users,
  selectedAccessMode,
  selectedEffectiveAccess,
  selectedEffectiveSource,
  onGlobalChange,
  onUserChange,
  onUserSelect,
  loading,
}) {
  const selectedOption = users.find((user) => user.username === selectedUser) || null;
  const accessStateLabel = selectedEffectiveAccess === "allow" ? "Permitido" : "Bloqueado";

  return (
    <Box sx={{ ...panelSx, p: "1vw", mb: "1vw" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1vw", mb: "0.9vw" }}>
        <Box>
          <Typography sx={kickerSx}>AI Access</Typography>
          <Typography sx={sectionTitleSx}>Acceso al asistente</Typography>
          <Typography sx={{ ...mutedSx, maxWidth: "42vw" }}>
            Estado efectivo: {accessStateLabel} por {selectedEffectiveSource}.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: "0.5vw", alignItems: "center" }}>
          <Button
            onClick={() => onGlobalChange("deny")}
            disabled={loading || globalAccess === "deny"}
            sx={globalAccess === "deny" ? activeButtonSx : secondaryButtonSx}
          >
            Global off
          </Button>
          <Button
            onClick={() => onGlobalChange("allow")}
            disabled={loading || globalAccess === "allow"}
            sx={globalAccess === "allow" ? activeButtonSx : secondaryButtonSx}
          >
            Global on
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: "0.8vw", alignItems: "center" }}>
        <Autocomplete
          freeSolo
          options={users}
          value={selectedOption || selectedUser || ""}
          inputValue={selectedUser || ""}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option?.username || ""
          }
          isOptionEqualToValue={(option, value) =>
            option.username === (typeof value === "string" ? value : value?.username)
          }
          onInputChange={(_, value) => onUserSelect(value || "")}
          onChange={(_, value) => {
            const nextUser = typeof value === "string" ? value : value?.username || "";
            onUserSelect(nextUser);
          }}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.username}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: "0.78vw", fontWeight: 700 }}>
                  {option.username}
                </Typography>
                <Typography sx={{ fontSize: "0.62vw", color: "#8fa0b5" }}>
                  {[option.cargo, option.correo].filter(Boolean).join(" · ") || "Usuario"}
                </Typography>
              </Box>
            </Box>
          )}
          PaperComponent={(props) => (
            <Box
              {...props}
              sx={{
                bgcolor: "#111a2a",
                color: "#d8e0ea",
                border: "0.01vw solid rgba(233,78,26,0.28)",
                "& .MuiAutocomplete-option": {
                  minHeight: "4.8vh",
                  "&[aria-selected='true']": { bgcolor: "rgba(233,78,26,0.16)" },
                  "&.Mui-focused": { bgcolor: "rgba(126,212,255,0.08)" },
                },
              }}
            />
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Usuario"
              sx={autocompleteSx}
              size="small"
              InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
            />
          )}
        />
        <Box sx={{ display: "flex", gap: "0.5vw", flexWrap: "wrap", justifyContent: "flex-end" }}>
          {[
            ["inherit", "Heredar"],
            ["allow", "Permitir"],
            ["deny", "Bloquear"],
          ].map(([value, label]) => (
            <Button
              key={value}
              onClick={() => onUserChange(value)}
              disabled={loading || !selectedUser || selectedAccessMode === value}
              sx={selectedAccessMode === value ? activeButtonSx : secondaryButtonSx}
            >
              {label}
            </Button>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function LimitEditor({
  title,
  description,
  limit,
  onChange,
  onSave,
  loading,
  users = [],
  lockIdentifier = false,
  currentSpend = 0,
}) {
  const selectedOption = users.find((user) => user.username === limit.identifier) || null;
  const monthlyBudget = Number(limit.monthly_budget_usd || 0);
  const warnAt = Number(limit.warn_at_percent || 0);
  const spendPercent = monthlyBudget ? Math.min(999, (Number(currentSpend || 0) / monthlyBudget) * 100) : 0;
  const warnActive = Boolean(monthlyBudget && warnAt && spendPercent >= warnAt);

  return (
    <Box sx={{ ...panelSx, p: "1vw" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "0.8vw" }}>
        <Box>
          <Typography sx={kickerSx}>{limit.scope}</Typography>
          <Typography sx={sectionTitleSx}>{title}</Typography>
          {description && <Typography sx={{ ...mutedSx, maxWidth: "34vw" }}>{description}</Typography>}
        </Box>
        <Tooltip title="Guardar limite">
          <span>
            <IconButton onClick={onSave} disabled={loading} sx={iconButtonSx}>
              <SaveRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.8vw" }}>
        {limit.scope === "user" && !lockIdentifier ? (
          <Autocomplete
            freeSolo
            options={users}
            value={selectedOption || limit.identifier || ""}
            inputValue={limit.identifier || ""}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option?.username || ""
            }
            isOptionEqualToValue={(option, value) =>
              option.username === (typeof value === "string" ? value : value?.username)
            }
            onInputChange={(_, value) => onChange({ identifier: value || "" })}
            onChange={(_, value) => {
              const nextUser = typeof value === "string" ? value : value?.username || "";
              onChange({ identifier: nextUser });
            }}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.username}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: "0.78vw", fontWeight: 700 }}>
                    {option.username}
                  </Typography>
                  <Typography sx={{ fontSize: "0.62vw", color: "#8fa0b5" }}>
                    {[option.cargo, option.correo].filter(Boolean).join(" · ") || "Usuario"}
                  </Typography>
                </Box>
              </Box>
            )}
            PaperComponent={(props) => (
              <Box
                {...props}
                sx={{
                  bgcolor: "#111a2a",
                  color: "#d8e0ea",
                  border: "0.01vw solid rgba(233,78,26,0.28)",
                  "& .MuiAutocomplete-option": {
                    minHeight: "4.8vh",
                    "&[aria-selected='true']": { bgcolor: "rgba(233,78,26,0.16)" },
                    "&.Mui-focused": { bgcolor: "rgba(126,212,255,0.08)" },
                  },
                }}
              />
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Usuario"
                sx={autocompleteSx}
                size="small"
                InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
              />
            )}
          />
        ) : (
          <TextField
            label={limit.scope === "user" ? "Usuario" : "Identificador"}
            value={limit.identifier}
            disabled={lockIdentifier}
            onChange={(event) => onChange({ identifier: event.target.value })}
            sx={inputSx}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        )}
        <TextField
          label="Cupo USD mensual"
          type="number"
          value={limit.monthly_budget_usd}
          onChange={(event) => onChange({ monthly_budget_usd: event.target.value })}
          sx={inputSx}
          size="small"
          inputProps={{ min: 0, step: "0.01" }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Umbral alerta (%)"
          type="number"
          value={limit.warn_at_percent}
          onChange={(event) => onChange({ warn_at_percent: event.target.value })}
          sx={inputSx}
          size="small"
          inputProps={{ min: 0, max: 100, step: 1 }}
          InputLabelProps={{ shrink: true }}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.7vw" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(limit.enabled)}
                onChange={(event) => onChange({ enabled: event.target.checked })}
                sx={{ color: "#e84e0e", "&.Mui-checked": { color: "#e84e0e" } }}
              />
            }
            label="Habilitado"
            sx={checkLabelSx}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(limit.hard_limit)}
                onChange={(event) => onChange({ hard_limit: event.target.checked })}
                sx={{ color: "#e84e0e", "&.Mui-checked": { color: "#e84e0e" } }}
              />
            }
            label="Bloqueo duro"
            sx={checkLabelSx}
          />
        </Box>
        <Box sx={{ gridColumn: "1 / -1" }}>
          <Typography sx={{ ...mutedSx, color: warnActive ? "#ffcc9d" : "#97a6b7" }}>
            Gasto actual: {money(currentSpend)}{monthlyBudget ? ` de ${money(monthlyBudget)} (${spendPercent.toFixed(1)}%)` : " · sin cupo configurado"}
            {warnActive ? " · umbral de alerta alcanzado" : ""}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function DataTable({ columns, rows, emptyText }) {
  return (
    <Box sx={{ maxHeight: "34vh", overflow: "auto", borderRadius: "0.9vw", border: "0.01vw solid rgba(233,78,26,0.16)", background: "rgba(0,0,0,0.12)" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        <thead>
          <tr>
            {columns.map(([label, width]) => (
              <th key={label} style={{ ...thSx, width }}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td key={`${rowIndex}-${cellIndex}`} style={{ ...tdSx, lineHeight: 1.45, overflowWrap: "anywhere" }}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} style={{ ...tdSx, textAlign: "center", padding: "2.2vh 1vw", color: "#8fa0b5" }}>
                {emptyText}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </Box>
  );
}

const kickerSx = { fontSize: "0.52vw", color: "#e94e1a", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase" };
const headingSx = { mt: "0.3vh", fontSize: "1.25vw", fontWeight: 700, color: "#f2f5f8" };
const sectionTitleSx = { mt: "0.15vh", fontSize: "1vw", fontWeight: 700, color: "#f2f5f8" };
const mutedSx = { mt: "0.45vh", fontSize: "0.72vw", color: "#97a6b7", lineHeight: 1.5 };
const metricSx = { borderRadius: "0.9vw", border: "0.01vw solid rgba(233,78,26,0.18)", background: "rgba(255,255,255,0.03)", px: "0.8vw", py: "0.9vh", minWidth: 0 };
const metricLabelSx = { fontSize: "0.54vw", color: "#8fa0b5", textTransform: "uppercase", letterSpacing: "0.08em" };
const metricValueSx = { mt: "0.35vh", fontSize: "1.1vw", fontWeight: 700, color: "#ffffff", overflowWrap: "anywhere" };
const iconButtonSx = { borderRadius: "0.85vw", border: "0.01vw solid rgba(233,78,26,0.24)", color: "#f2f5f8", background: "rgba(255,255,255,0.03)", "&:hover": { background: "rgba(233,78,26,0.13)" } };
const primaryButtonSx = { borderRadius: "0.8vw", minHeight: "4.1vh", textTransform: "none", fontSize: "0.76vw", fontWeight: 700, color: "#fff", background: "linear-gradient(135deg, #ff6a2f, #e84e0e)", "&:hover": { background: "linear-gradient(135deg, #ff7d4c, #f05f26)" } };
const secondaryButtonSx = { borderRadius: "0.8vw", minHeight: "4.1vh", px: "0.9vw", textTransform: "none", fontSize: "0.72vw", fontWeight: 700, color: "#d8e0ea", border: "0.01vw solid rgba(233,78,26,0.24)", background: "rgba(255,255,255,0.03)", "&:hover": { background: "rgba(233,78,26,0.13)" }, "&.Mui-disabled": { color: "rgba(216,224,234,0.38)", borderColor: "rgba(233,78,26,0.12)" } };
const activeButtonSx = { ...secondaryButtonSx, color: "#ffffff", borderColor: "rgba(233,78,26,0.58)", background: "rgba(233,78,26,0.22)" };
const tableTitleSx = { mb: "0.8vw", fontSize: "1vw", fontWeight: 700, color: "#f2f5f8" };
const checkLabelSx = { m: 0, "& .MuiTypography-root": { fontSize: "0.68vw", color: "#d4dde8" } };
